import { Task, FullProject, ProjectMetadata } from '../../types';
import { getHolidaysForYear } from '../../utils/holidayUtils';
import { isMilestoneOrDeadlineView, isPersonContactView } from '../../domain/taskGuards';
import { normalizePersonName } from '../../domain/taskFilterEngine';

export type SearchCategoryType = 'all' | 'task' | 'person' | 'inbox' | 'milestone';

/**
 * Builds O(1) lookup Map from project ID (and rawId) to FullProject.
 */
export function buildProjectsByIdMap(allProjects: FullProject[]): Map<string, FullProject> {
  const map = new Map<string, FullProject>();
  for (let i = 0; i < allProjects.length; i++) {
    const proj = allProjects[i];
    map.set(proj.id, proj);
    if (proj.rawId) {
      map.set(proj.rawId, proj);
    }
  }
  return map;
}

/**
 * Builds O(1) lookup Map from stringified task ID to Task.
 */
export function buildTasksByIdMap(allTasks: Task[]): Map<string, Task> {
  const map = new Map<string, Task>();
  for (let i = 0; i < allTasks.length; i++) {
    const t = allTasks[i];
    map.set(String(t.id), t);
  }
  return map;
}

/**
 * Matches projects, graph nodes, inbox items, and processed items against search query `q`.
 */
export function buildProjectAndNodeSearchMatches(params: {
  query: string;
  allProjects: FullProject[];
  globalMetadata: ProjectMetadata;
  activeWorkspace?: 'professional' | 'private';
  showArchived: boolean;
}): Task[] {
  const { query, allProjects, globalMetadata, activeWorkspace, showArchived } = params;
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];

  const pMatches = allProjects
    .filter((p) => {
      if (!showArchived && p.archived) return false;
      return p.name.toLowerCase().includes(q);
    })
    .map(
      (p) =>
        ({
          id: p.id,
          name: p.name,
          projectId: p.id,
          type: 'project' as Task['type'],
          category: 'Projekt',
          description: 'Projekt-Übersicht',
        }) as Task
    );

  const nMatches = (globalMetadata.graphNodes || [])
    .filter((n) => n.label.toLowerCase().includes(q))
    .map(
      (n) =>
        ({
          id: n.id,
          name: n.label,
          projectId: 'inbox_inforum',
          type: 'contact' as Task['type'],
          category: 'Knotenpunkt',
          description: 'Netzwerk-Knoten',
        }) as Task
    );

  const inboxItems = globalMetadata.inboxItems || [];
  const processedItems = globalMetadata.processedItems || [];

  const filteredInbox = !activeWorkspace
    ? inboxItems
    : inboxItems.filter(
        (i) => i.workspace === activeWorkspace || (!i.workspace && activeWorkspace === 'professional')
      );

  const filteredProcessed = !activeWorkspace
    ? processedItems
    : processedItems.filter(
        (i) => i.workspace === activeWorkspace || (!i.workspace && activeWorkspace === 'professional')
      );

  const inboxMatches = filteredInbox
    .filter(
      (i) =>
        (i.title || '').toLowerCase().includes(q) || (i.content || '').toLowerCase().includes(q)
    )
    .map(
      (i) =>
        ({
          id: i.id,
          name: i.title || 'Mitteilung',
          projectId: 'inbox_inforum',
          type: 'memo' as Task['type'],
          category: 'Eingang',
          description: i.content || '',
        }) as Task
    );

  const processedMatches = filteredProcessed
    .filter(
      (i) =>
        (i.title || '').toLowerCase().includes(q) || (i.content || '').toLowerCase().includes(q)
    )
    .map(
      (i) =>
        ({
          id: i.id,
          name: i.title || 'Chronik-Eintrag',
          projectId: 'inbox_inforum',
          type: 'memo' as Task['type'],
          category: 'Chronik',
          description: i.content || '',
        }) as Task
    );

  return [...pMatches, ...nMatches, ...inboxMatches, ...processedMatches];
}

/**
 * Computes combined search results using O(1) project lookup (`projectsByIdMap`).
 */
export function computeCombinedSearchResults(params: {
  query: string;
  searchCategory: SearchCategoryType;
  showArchived: boolean;
  projectAndNodeMatches: Task[];
  candidateTasks: Task[];
  hasFreshWorkerResults: boolean;
  projectsByIdMap: Map<string, FullProject>;
}): Task[] {
  const {
    query,
    searchCategory,
    showArchived,
    projectAndNodeMatches,
    candidateTasks,
    hasFreshWorkerResults,
    projectsByIdMap,
  } = params;
  const q = query.trim().toLowerCase();
  if (!q) return [];

  let filteredMetadata = projectAndNodeMatches;
  if (searchCategory === 'person') {
    filteredMetadata = projectAndNodeMatches.filter((item) => item.category === 'Knotenpunkt');
  } else if (searchCategory === 'inbox') {
    filteredMetadata = projectAndNodeMatches.filter(
      (item) => item.category === 'Eingang' || item.category === 'Chronik'
    );
  } else if (searchCategory === 'task' || searchCategory === 'milestone') {
    filteredMetadata = [];
  }

  const taskMatches = candidateTasks.filter((t) => {
    const parentProj = t.projectId ? projectsByIdMap.get(t.projectId) : undefined;
    const isParentProjectArchived = parentProj ? !!parentProj.archived : false;
    if (!showArchived && (t.archived || isParentProjectArchived)) return false;

    if (searchCategory === 'inbox') {
      return false;
    }

    if (hasFreshWorkerResults) {
      if (searchCategory === 'task') {
        return !isPersonContactView(t) && !isMilestoneOrDeadlineView(t);
      }
      if (searchCategory === 'milestone') {
        return isMilestoneOrDeadlineView(t);
      }
      if (searchCategory === 'person') {
        const nameMatch = (t.name || '').toLowerCase().includes(q);
        const assigneeMatch = (t.assignee || '').toLowerCase().includes(q);
        return assigneeMatch || nameMatch;
      }
      return true;
    }

    const nameMatch = (t.name || '').toLowerCase().includes(q);
    const assigneeMatch = (t.assignee || '').toLowerCase().includes(q);
    const orgMatch = (t.organization || '').toLowerCase().includes(q);
    const subtitleMatch = (t.subtitle || '').toLowerCase().includes(q);
    const descMatch = (t.description || '').toLowerCase().includes(q);
    const fullMatch = nameMatch || assigneeMatch || orgMatch || subtitleMatch || descMatch;

    if (searchCategory === 'task') {
      if (isPersonContactView(t) || isMilestoneOrDeadlineView(t)) return false;
      return fullMatch;
    }
    if (searchCategory === 'milestone') {
      if (!isMilestoneOrDeadlineView(t)) return false;
      return fullMatch;
    }
    if (searchCategory === 'person') {
      return assigneeMatch || nameMatch;
    }

    return fullMatch;
  });

  let holidayMatches: Task[] = [];
  if (searchCategory === 'all') {
    const currentYear = new Date().getFullYear();
    const rawHolidays = [
      ...getHolidaysForYear(currentYear),
      ...getHolidaysForYear(currentYear + 1),
      ...getHolidaysForYear(currentYear + 2),
    ];

    const now = new Date();
    const y = now.getFullYear();
    const mStr = String(now.getMonth() + 1).padStart(2, '0');
    const dStr = String(now.getDate()).padStart(2, '0');
    const todayStr = `${y}-${mStr}-${dStr}`;

    const sortedHolidays = rawHolidays.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
    const futureHolidays: typeof rawHolidays = [];
    const seenNames = new Set<string>();

    for (const h of sortedHolidays) {
      if (h.dateStr >= todayStr && !seenNames.has(h.name)) {
        seenNames.add(h.name);
        futureHolidays.push(h);
      }
    }

    holidayMatches = futureHolidays
      .filter(
        (h) => h.name.toLowerCase().includes(q) || (h.description || '').toLowerCase().includes(q)
      )
      .map(
        (h) =>
          ({
            id: `holiday_${h.dateStr}_${h.name.replace(/\s/g, '_')}`,
            name: h.name,
            projectId: 'dashboard',
            type: 'holiday' as Task['type'],
            category: 'Feiertag',
            description: h.description || '',
            start: h.dateStr,
            end: h.dateStr,
            completed: false,
            status: 'open',
          }) as Task
      );
  }

  const allFiltered = [...filteredMetadata, ...taskMatches, ...holidayMatches];
  const seenIds = new Set<string>();
  const deduplicatedFiltered = allFiltered.filter((item) => {
    const idStr = String(item.id);
    if (seenIds.has(idStr)) return false;
    seenIds.add(idStr);
    return true;
  });

  const finalResults =
    deduplicatedFiltered.length > 500 ? deduplicatedFiltered.slice(0, 500) : deduplicatedFiltered;

  return finalResults.sort((a, b) => {
    let scoreA = 0;
    let scoreB = 0;
    if (a.category === 'Projekt') scoreA += 500;
    if (b.category === 'Projekt') scoreB += 500;
    if (a.category === 'Eingang') scoreA += 400;
    if (b.category === 'Eingang') scoreB += 400;
    if (a.category === 'Knotenpunkt') scoreA += 300;
    if (b.category === 'Knotenpunkt') scoreB += 300;
    if (a.category === 'Chronik') scoreA += 200;
    if (b.category === 'Chronik') scoreB += 200;
    if (a.category === 'Feiertag') scoreA += 150;
    if (b.category === 'Feiertag') scoreB += 150;
    if ((a.name || '').toLowerCase().includes(q)) scoreA += 100;
    if ((b.name || '').toLowerCase().includes(q)) scoreB += 100;
    if ((a.name || '').toLowerCase() === q) scoreA += 1000;
    if ((b.name || '').toLowerCase() === q) scoreB += 1000;
    return scoreB - scoreA;
  });
}

/**
 * Extracts unique sorted assignee names across current tasks and active workspace projects,
 * deduplicating variants that share the same normalized person name (M-7).
 */
export function extractUniqueWorkspaceAssignees(
  tasks: Task[],
  allProjects: FullProject[],
  activeWorkspace?: 'professional' | 'private'
): string[] {
  const byNorm = new Map<string, string>();
  const projectWorkspaceById = new Map<string, 'professional' | 'private'>();
  allProjects.forEach((p) => {
    const ws = (p.workspace || 'professional') as 'professional' | 'private';
    projectWorkspaceById.set(p.id, ws);
    if (p.rawId) projectWorkspaceById.set(p.rawId, ws);
  });

  const addNames = (raw?: string) => {
    if (!raw) return;
    raw.split(',').forEach((a) => {
      const trimmed = a.trim();
      if (!trimmed || trimmed === 'all') return;
      const norm = normalizePersonName(trimmed) || trimmed.toLowerCase();
      const existing = byNorm.get(norm);
      if (!existing || trimmed.length > existing.length) {
        byNorm.set(norm, trimmed);
      }
    });
  };

  tasks.forEach((t) => {
    if (activeWorkspace) {
      const taskWs =
        t.workspace ||
        (t.projectId ? projectWorkspaceById.get(t.projectId) : undefined) ||
        activeWorkspace;
      if (taskWs !== activeWorkspace) return;
    }
    if (t.type === 'contact' || t.status === 'pool') {
      addNames(t.name || t.assignee);
    } else {
      addNames(t.assignee);
    }
  });
  allProjects.forEach((p) => {
    if (p.archived) return;
    if (activeWorkspace && (p.workspace || 'professional') !== activeWorkspace) return;
    (p.tasks || []).forEach((t) => {
      if (t.archived) return;
      if (t.workspace && activeWorkspace && t.workspace !== activeWorkspace) return;
      if (t.type === 'contact' || t.status === 'pool') {
        addNames(t.name || t.assignee);
      } else {
        addNames(t.assignee);
      }
    });
  });
  return Array.from(byNorm.values()).sort((a, b) => a.localeCompare(b, 'de'));
}
